import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { Contact } from '@/api/types/contacts';
import { Icon, IconButton, MenuDivider, MenuSectionLabel, Popover } from '@/components/ui';

import type { SectionDef, SectionGroup } from './sections/registry';

const GROUP_LABELS: Record<SectionGroup, string> = {
  core: 'Core',
  professional: 'Professional',
  engagement: 'Engagement',
  meta: 'Meta',
};

const GROUP_ORDER: SectionGroup[] = ['core', 'professional', 'engagement', 'meta'];

interface SectionsMenuProps {
  visibleSections: SectionDef[];
  emptySections: SectionDef[];
  contact: Contact | undefined;
  onReorder: (nextOrder: string[]) => void;
  onDisable: (key: string) => void;
  onEnable: (key: string) => void;
}

export function SectionsMenu({
  visibleSections,
  emptySections,
  contact,
  onReorder,
  onDisable,
  onEnable,
}: SectionsMenuProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = visibleSections.map((s) => s.key);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const next = [...ids];
    const [moved] = next.splice(oldIdx, 1);
    next.splice(newIdx, 0, moved);
    onReorder(next);
  };

  const grouped = GROUP_ORDER.map((g) => ({
    group: g,
    items: emptySections.filter((s) => s.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <Popover
      placement="bottom-end"
      closeOnClickInside={false}
      trigger={
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm">
          <Icon name="columns" size={13} />
          Sections
          <Icon name="chevronDown" size={11} />
        </button>
      }
    >
      <div style={{ width: 280, maxHeight: 480, overflowY: 'auto' }}>
        <MenuSectionLabel>Visible — drag to reorder</MenuSectionLabel>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={visibleSections.map((s) => s.key)}
            strategy={verticalListSortingStrategy}
          >
            {visibleSections.map((s) => (
              <SortableRow key={s.key} section={s} contact={contact} onDisable={onDisable} />
            ))}
          </SortableContext>
        </DndContext>

        {grouped.length > 0 && (
          <>
            <MenuDivider />
            <MenuSectionLabel>Add a section</MenuSectionLabel>
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <div
                  style={{
                    fontSize: 10.5,
                    color: 'var(--text-4)',
                    padding: '4px 10px 2px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {GROUP_LABELS[group]}
                </div>
                {items.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className="ds-menu-item"
                    onClick={() => {
                      onEnable(s.key);
                      requestAnimationFrame(() => {
                        document.getElementById(`section-${s.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }}
                  >
                    <Icon name="plus" size={12} />
                    {s.label}
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </Popover>
  );
}

function SortableRow({
  section,
  contact,
  onDisable,
}: {
  section: SectionDef;
  contact: Contact | undefined;
  onDisable: (key: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.key,
  });
  const hasData = !section.isEmpty(contact);
  const canHide = !section.required;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="ds-menu-item"
    >
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', touchAction: 'none', color: 'var(--text-4)', display: 'inline-flex' }}
        aria-label="Drag to reorder"
      >
        <Icon name="moreH" size={12} />
      </span>
      <button
        type="button"
        onClick={() => {
          document.getElementById(`section-${section.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        style={{
          flex: 1,
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {section.label}
      </button>
      {canHide && (
        <IconButton
          icon={hasData ? 'eye' : 'trash'}
          iconSize={12}
          size="sm"
          label={hasData ? 'Hide section' : 'Remove section'}
          onClick={() => onDisable(section.key)}
        />
      )}
    </div>
  );
}
